import { MessageList } from "@/components/ui/aimessagelist"
import AISearchInput from "@/components/ui/aisearchinput"
import SearchConfirmation from "@/components/ui/searchconfirmation"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AiChatPageProps, Message, SearchCriterion, SearchConfirmationProps } from "./types"
import LacleoIcon from "../../static/media/avatars/lacleo_avatar.svg?react"
import LoaderLines from "../../static/media/icons/loader-lines.svg?react"
import {
  applyCriteria,
  finishCriteriaProcessing,
  finishSearch,
  startSearch,
  selectIsProcessingCriteria,
  selectSearchQuery
} from "./slice/searchslice"
import { useGetFiltersQuery } from "../filters/slice/apiSlice"
import { addSelectedItem, resetFilters } from "../filters/slice/filterSlice"
import { IFilterGroup } from "@/interface/filters/filterGroup"
import { useExtractFiltersMutation } from "./slice/apiSlice"

const LOADING_PHRASES: string[] = [
  "Curating filters for your search…",
  "Finding the most relevant results…",
  "Scanning through data…",
  "Sifting through possibilities…",
  "Matching the best options for you…",
  "Refining your filters…",
  "Analyzing search parameters…",
  "Narrowing it down for you…",
  "Exploring all possibilities…",
  "Gathering tailored suggestions…",
  "Almost there…",
  "Finalizing your matches…"
]

const AiChatPage: React.FC<AiChatPageProps> = ({ initialQuery, onBackToHome }) => {
  const dispatch = useDispatch()
  const { currentData: filterGroups = [] as IFilterGroup[] } = useGetFiltersQuery()

  // Local state for messages and UI
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentCriteria, setCurrentCriteria] = useState<SearchCriterion[]>([])
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0)

  // Redux selectors
  const isProcessingCriteria = useSelector(selectIsProcessingCriteria)
  const reduxSearchQuery = useSelector(selectSearchQuery)

  // Track the last processed initial query to prevent duplicates
  const lastProcessedInitialQuery = useRef<string | null>(null)
  const isProcessingRef = useRef(false)
  const mountedRef = useRef(false)

  const [extractFilters] = useExtractFiltersMutation()

  // Memoize the criteria extraction function
  const extractCriteriaFromPrompt = useCallback(
    async (prompt: string): Promise<SearchCriterion[]> => {
      const groups = await extractFilters({ query: prompt, variables: {} }).unwrap()
      const criteria = groups
        .flatMap((g) => g.filters)
        .filter((f) => f && f.value !== null && String(f.value).trim() !== "")
        .map((f) => ({ id: f.id, label: f.name, value: String(f.value), checked: true }))

      if (criteria.length === 0) {
        return [
          {
            id: "general",
            label: "Search Type",
            value: "General Search",
            checked: true
          }
        ]
      }

      return criteria
    },
    [extractFilters]
  )

  const addMessage = useCallback((message: Omit<Message, "id" | "timestamp">) => {
    const newMessage: Message = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date()
    }

    setMessages((prev) => [...prev, newMessage])
    return newMessage
  }, [])

  const disablePreviousConfirmations = useCallback(() => {
    setMessages((prev) =>
      prev.map((message) => {
        const componentNode = message.component
        if (componentNode && React.isValidElement(componentNode) && componentNode.type === SearchConfirmation) {
          const element = componentNode as React.ReactElement<SearchConfirmationProps>
          return {
            ...message,
            component: React.cloneElement<SearchConfirmationProps>(element, { disabled: true })
          }
        }
        return message
      })
    )
  }, [])

  // Memoized handlers
  const handleCriterionChange = useCallback((id: string, checked: boolean) => {
    setCurrentCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, checked } : c)))
  }, [])

  const handleApplySearch = useCallback(
    async (criteria: SearchCriterion[]) => {
      if (!mountedRef.current) return

      setIsLoading(true)

      // Update AI slice (show results, set loading flags)
      dispatch(applyCriteria(criteria))

      try {
        // Reset existing filters
        dispatch(resetFilters())

        // Map AI criteria → Filters slice
        const allFilters = (filterGroups || []).flatMap((g) => g.filters)
        criteria
          .filter((c) => c.checked)
          .forEach((c) => {
            const matched =
              allFilters.find((f) => f.name.toLowerCase() === c.label.toLowerCase()) ||
              allFilters.find((f) => f.id.toLowerCase() === c.id.toLowerCase())

            if (matched) {
              dispatch(
                addSelectedItem({
                  sectionId: matched.id,
                  item: { id: c.value, name: c.value, type: "include" }
                })
              )
            }
          })

        if (!mountedRef.current) return

        // Finish search (remove loading)
        dispatch(finishSearch())
      } catch (error) {
        console.error("Error applying search:", error)
        if (mountedRef.current) {
          addMessage({
            type: "ai",
            content: "Sorry, there was an error executing your search. Please try again."
          })
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [addMessage, dispatch, filterGroups]
  )

  // Rotate loading phrases while loading
  useEffect(() => {
    if (!isLoading) {
      setLoadingPhraseIndex(0)
      return
    }
    let nextIndex = 0
    setLoadingPhraseIndex(0)
    const intervalId = setInterval(() => {
      nextIndex = (nextIndex + 1) % LOADING_PHRASES.length
      setLoadingPhraseIndex(nextIndex)
    }, 1500)
    return () => clearInterval(intervalId)
  }, [isLoading])

  const processQuery = useCallback(
    async (query: string, isInitial = false) => {
      // Prevent processing if already processing or if this is a duplicate initial query
      if (isProcessingRef.current) {
        console.log("Already processing, skipping...")
        return
      }

      if (isInitial && lastProcessedInitialQuery.current === query) {
        console.log("Initial query already processed, skipping...")
        return
      }

      console.log("Processing query:", query, "isInitial:", isInitial)

      isProcessingRef.current = true
      if (isInitial) {
        lastProcessedInitialQuery.current = query
      }

      setIsLoading(true)

      // Add user message
      addMessage({
        type: "user",
        content: query
      })

      try {
        // Extract criteria from query
        const criteria = await extractCriteriaFromPrompt(query)

        if (!mountedRef.current) return

        setCurrentCriteria(criteria)

        // Dispatch that criteria processing is finished
        dispatch(finishCriteriaProcessing())

        // Create a stable component instance to prevent re-renders
        const confirmationComponent = (
          <SearchConfirmation criteria={criteria} onApply={handleApplySearch} onCriterionChange={handleCriterionChange} disabled={false} />
        )

        // Add confirmation message with component
        addMessage({
          type: "system",
          content: "Please confirm your search criteria:",
          component: confirmationComponent
        })
      } catch (error) {
        console.error("Error processing query:", error)
        if (mountedRef.current) {
          addMessage({
            type: "ai",
            content: "Sorry, I encountered an error processing your request. Please try again."
          })
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false)
        }
        isProcessingRef.current = false
      }
    },
    [addMessage, extractCriteriaFromPrompt, handleApplySearch, handleCriterionChange, dispatch]
  )

  // Handle initial query processing
  useEffect(() => {
    mountedRef.current = true

    const queryToProcess = initialQuery || reduxSearchQuery

    if (queryToProcess && queryToProcess.trim() && lastProcessedInitialQuery.current !== queryToProcess) {
      console.log("Processing initial query:", queryToProcess)
      processQuery(queryToProcess, true)
    }

    return () => {
      mountedRef.current = false
    }
  }, [initialQuery, reduxSearchQuery, processQuery])

  // Reset when initialQuery changes to a new value
  useEffect(() => {
    const queryToCheck = initialQuery || reduxSearchQuery
    if (queryToCheck && lastProcessedInitialQuery.current && lastProcessedInitialQuery.current !== queryToCheck) {
      console.log("Query changed, resetting...")
      lastProcessedInitialQuery.current = null
      isProcessingRef.current = false
    }
  }, [initialQuery, reduxSearchQuery])

  const handleNewSearch = useCallback(
    (query: string) => {
      console.log("New search:", query)
      // Disable previous confirmation Apply buttons
      disablePreviousConfirmations()
      // Dispatch Redux action for new search
      dispatch(startSearch(query))
      // Reset refs for new searches
      lastProcessedInitialQuery.current = null
      processQuery(query, false)
    },
    [processQuery, dispatch, disablePreviousConfirmations]
  )

  return (
    <div className="flex h-full flex-1 flex-col">
      {/* Messages Container */}
      <div className="flex max-h-[calc(100vh-363px)] min-h-0 flex-1 flex-col overflow-auto">
        <MessageList messages={messages} />

        {/* Loading indicator */}
        {!!isLoading && (
          <div className="ml-6 flex items-end gap-[10px]">
            <LacleoIcon />
            <div className="rounded-[12px] border border-[#EBEBEB] px-6 py-[18px]">
              <span className="flex items-center gap-2 text-base font-normal text-[#5C5C5C]">
                <LoaderLines className="size-8" />
                {LOADING_PHRASES[loadingPhraseIndex]}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Search Input at bottom */}
      <div className="bg-white p-4">
        <AISearchInput onSearch={handleNewSearch} placeholder="Ask a follow-up question or start a new search..." disabled={isLoading} />
      </div>
    </div>
  )
}

export default AiChatPage
